import { isSameDay, isValid } from 'date-fns'
import { TFunction } from 'i18next'
import { useEffect } from 'react'

import { StatusTabOption } from '@/components/ui/status-tabs'
import { SortOrder } from '@/components/ui/table'
import { EMPTY_VALUE } from '@/constants/ui'
import { useFilters } from '@/hooks'
import i18n from '@/i18n'
import { VisitorStatus, visitorStatusBadgeColorMap } from '@/models'
import { formatDateOnly, formatTimeOnly, formatTimeRange } from '@/utils'

export const formatDateTime = (
  dateTimeValue: number | string | null | undefined,
  timezone?: string,
) => {
  if (!dateTimeValue) return { date: EMPTY_VALUE, time: EMPTY_VALUE }

  const date =
    typeof dateTimeValue === 'number' ? new Date(Number(dateTimeValue)) : new Date(dateTimeValue)

  if (!isValid(date)) return { date: EMPTY_VALUE, time: EMPTY_VALUE }

  return {
    date: formatDateOnly(date, timezone),
    time: formatTimeOnly(date, timezone),
  }
}

type VisitorForPermissions = {
  mode?: string
  startTime?: string
  endTime?: string
  monthlyPermission?: string
  periodicity?: string | string[] | number[]
}

export const formatPermissions = (visitor: VisitorForPermissions, timezone?: string) => {
  if (!visitor.mode) return EMPTY_VALUE

  const mode = visitor.mode.toLowerCase()

  switch (mode) {
    case 'custom': {
      if (visitor.startTime && visitor.endTime) {
        const startDate = new Date(visitor.startTime)
        const endDate = new Date(visitor.endTime)

        if (!isValid(startDate) || !isValid(endDate)) return EMPTY_VALUE

        if (isSameDay(startDate, endDate)) {
          return formatDateOnly(startDate, timezone)
        }

        return `${formatDateOnly(startDate, timezone)} - ${formatDateOnly(endDate, timezone)}`
      }
      if (visitor.startTime) {
        const date = new Date(visitor.startTime)
        if (!isValid(date)) return EMPTY_VALUE
        return formatDateOnly(date, timezone)
      }
      return i18n.t('visitors:permissions.oneTimeVisit')
    }

    case 'daily':
      return i18n.t('visitors:permissions.daily')

    case 'monthly':
      return visitor.monthlyPermission || i18n.t('visitors:permissions.monthly')

    case 'weekly': {
      let days: number[] = []
      if (visitor.periodicity) {
        if (Array.isArray(visitor.periodicity)) {
          if (visitor.periodicity.length === 0) {
            return i18n.t('visitors:permissions.weekly')
          } else if (
            visitor.periodicity.length === 1 &&
            typeof visitor.periodicity[0] === 'string'
          ) {
            days = (visitor.periodicity[0] as string).split(',').map((d) => Number(d.trim()))
          } else {
            days = (visitor.periodicity as (string | number)[]).map(Number)
          }
        } else if (typeof visitor.periodicity === 'string' && visitor.periodicity !== '') {
          days = visitor.periodicity.split(',').map((d) => Number(d.trim()))
        }
      }
      if (days.length > 0 && days.every((d) => !isNaN(d))) {
        const dayNames = [
          i18n.t('visitors:days.sun'),
          i18n.t('visitors:days.mon'),
          i18n.t('visitors:days.tue'),
          i18n.t('visitors:days.wed'),
          i18n.t('visitors:days.thu'),
          i18n.t('visitors:days.fri'),
          i18n.t('visitors:days.sat'),
        ]
        return days.map((day) => dayNames[day]).join(', ')
      }
      return i18n.t('visitors:permissions.weekly')
    }

    default:
      return mode.charAt(0).toUpperCase() + mode.slice(1)
  }
}

// Note: "Always" access has no schedule at all, so there's no start/end time to show
// alongside it - pairing formatPermissions with formatTimeRange unconditionally left a
// stray "-" (formatTimeRange's empty-value fallback) next to "Always".
export const formatPermissionTimeRange = (visitor: VisitorForPermissions, timezone?: string) => {
  if (visitor.mode?.toLowerCase() === 'always') return null

  return formatTimeRange(visitor.startTime, visitor.endTime, timezone)
}

export const emptyOptionText = '---'

type VisitorsSortFilters = {
  sortBy?: string
  sortOrder?: SortOrder
}

// Note: the Permissions column is the default sort for the Visitors tables (guests, vendors,
// history) - without seeding it into the filters/URL state, no column's sortKey matches the
// table's sortBy/sortOrder, so the sort indicator never appears even though the table is sorted.
const DEFAULT_SORT_BY = 'permission'
const DEFAULT_SORT_ORDER: SortOrder = 'desc'

export const useDefaultVisitorsSort = <T extends VisitorsSortFilters>({
  searchParams,
  filters,
}: {
  searchParams: { parsedQuery: VisitorsSortFilters }
  filters: ReturnType<typeof useFilters<T>>
}) => {
  useEffect(() => {
    if (searchParams.parsedQuery.sortBy || searchParams.parsedQuery.sortOrder) {
      return
    }

    filters.setFilters((prev) => ({
      ...prev,
      sortBy: DEFAULT_SORT_BY,
      sortOrder: DEFAULT_SORT_ORDER,
    }))
  }, [])
}

// Note: the Guests and Vendors status tabs - an uncolored All tab summing every status, then one
// tab per status colored like its row badge (EL-10661).
export const getVisitorStatusTabs = (
  t: TFunction<['visitors']>,
  countByStatus: Record<VisitorStatus, number> | undefined,
): StatusTabOption<VisitorStatus | 'all'>[] => {
  const statusLabels: Record<VisitorStatus, string> = {
    active: t('visitors:statuses.active'),
    banned: t('visitors:statuses.banned'),
    expired: t('visitors:statuses.expired'),
    suspended: t('visitors:statuses.suspended'),
  }
  const statusOrder: VisitorStatus[] = ['active', 'expired', 'banned', 'suspended']

  return [
    {
      count: countByStatus
        ? Object.values(countByStatus).reduce((acc, curr) => acc + curr, 0)
        : undefined,
      label: t('visitors:statuses.all'),
      value: 'all',
    },
    ...statusOrder.map((status) => ({
      color: visitorStatusBadgeColorMap[status],
      count: countByStatus?.[status],
      label: statusLabels[status],
      value: status,
    })),
  ]
}
